import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'WebflowIntegrationForm',
  props: {
    contentplanId: {
      type: Number,
      required: true
    },
    workspaceId: {
      type: Number,
      default: null
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showHelp: {
      type: Boolean,
      default: true
    },
    allowDelete: {
      type: Boolean,
      default: true
    },
    allowAuth: {
      type: Boolean,
      default: true
    },
    initialSiteId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      isLoadingSites: false,
      isLoadingCollections: false,
      isLoadingSchema: false,
      isSavingApiKey: false,
      isSavingSettings: false,
      isDeleting: false,
      apiKey: '',
      apiKeyError: null,
      sitesFetchSuccess: false,
      hasAutoFetchedSites: false,
      sites: [],
      collections: [],
      collectionSchemaFields: [],
      selectedSiteId: null,
      selectedCollectionId: null,
      selectedSchemaBody: null,
      selectedSchemaSummary: null,
      selectedSchemaImage: null,
      wfAutoPublish: false,
      rules: {
        required: value => !!value || 'Required.'
      },
      errorFetchingSites: null,
      errorFetchingCollections: null,
      errorFetchingSchema: null
    }
  },
  computed: {
    ...mapGetters([
      'webflowIntegration'
    ]),
    activeIntegrationSiteId () {
      return this.webflowIntegration?.wf_site_id
    },
    activeIntegrationCollectionId () {
      return this.webflowIntegration?.wf_collection_id
    },
    activeIntegrationSchemaBody () {
      return this.webflowIntegration?.wf_schema_body
    },
    activeIntegrationSchemaSummary () {
      return this.webflowIntegration?.wf_schema_summary
    },
    activeIntegrationSchemaImage () {
      return this.webflowIntegration?.wf_schema_image
    },
    activeIntegrationAutoPublish () {
      return !!this.webflowIntegration?.wf_auto_publish
    },
    siteIsPreselected () {
      return !!this.selectedSiteId
    },
    selectedSiteDisplayName () {
      if (!this.selectedSiteId) return ''
      const selectedSite = this.sites.find(site => String(site.id) === String(this.selectedSiteId))
      if (!selectedSite) return this.selectedSiteId
      return selectedSite.displayName || selectedSite.name || selectedSite.id
    },
    isAuthorized () {
      return this.webflowIntegration?.type === 'webflow'
    },
    hasActiveConfig () {
      return this.webflowIntegration?.type === 'webflow' &&
        !!this.webflowIntegration?.wf_site_id &&
        !!this.webflowIntegration?.wf_collection_id &&
        !!this.webflowIntegration?.wf_schema_body &&
        !!this.webflowIntegration?.wf_schema_summary
    },
    showConnectButton () {
      if (!this.allowAuth) return false
      return !this.isAuthorized
    },
    bodySchemaFields () {
      return this.collectionSchemaFields.filter(field => field.type === 'RichText')
    },
    summarySchemaFields () {
      return this.collectionSchemaFields.filter(field => field.type === 'PlainText' && field.slug !== 'name' && field.slug !== 'slug')
    },
    imageSchemaFields () {
      return this.collectionSchemaFields.filter(field => field.type === 'ImageRef' || field.type === 'Image')
    },
    isValidToFetchSites () {
      return this.isAuthorized
    },
    isValidToFetchCollections () {
      return !!this.selectedSiteId && this.isAuthorized
    },
    isValidToFetchSchema () {
      return !!this.selectedCollectionId && this.isAuthorized
    },
    isValidToSave () {
      return this.isAuthorized && !!this.selectedSiteId && !!this.selectedCollectionId && !!this.selectedSchemaBody && !!this.selectedSchemaSummary
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'postWebflowIntegration',
      'deleteIntegration',
      'getWebflowSites',
      'getWebflowCollections',
      'getWebflowCollectionSchema'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    async saveApiKey () {
      if (!this.apiKey || this.isSavingApiKey) return

      this.isSavingApiKey = true
      this.apiKeyError = null

      try {
        await this.postWebflowIntegration({
          action: 'save-api-key',
          contentplanId: this.contentplanId,
          apiKey: this.apiKey
        })
        this.apiKey = ''
        this.hasAutoFetchedSites = false
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Webflow API token saved.', type: 'success' })
      } catch (error) {
        console.error('Error saving Webflow API token:', error)
        this.apiKeyError = error?.response?.data?.message || error.message || 'Could not save Webflow API token'
        this.setSnackbar({ open: true, text: this.apiKeyError, type: 'error' })
      } finally {
        this.isSavingApiKey = false
      }
    },
    async triggerFetchSites () {
      if (!this.isValidToFetchSites) return

      const preferredSiteId = this.selectedSiteId || this.initialSiteId
      this.isLoadingSites = true
      this.sites = []
      this.collections = []
      this.selectedSiteId = null
      this.selectedCollectionId = null
      this.errorFetchingSites = null
      this.errorFetchingCollections = null
      this.errorFetchingSchema = null
      this.collectionSchemaFields = []
      this.selectedSchemaBody = null
      this.selectedSchemaSummary = null
      this.selectedSchemaImage = null
      this.sitesFetchSuccess = false

      try {
        const fetchedSites = await this.getWebflowSites({ contentplanId: this.contentplanId })
        this.sites = (fetchedSites || []).map(site => {
          const label = site.displayName || site.name || site.shortName || 'Unnamed site'
          return {
            ...site,
            displayName: `${label} (${site.id})`
          }
        })
        if (this.sites.length === 0) {
          this.errorFetchingSites = 'No Webflow sites found. Check your permissions.'
        } else {
          this.sitesFetchSuccess = true
          const matchedSite = this.sites.find(site => String(site.id) === String(preferredSiteId))
          if (matchedSite) this.selectedSiteId = matchedSite.id
          if (!matchedSite && this.sites.length === 1) this.selectedSiteId = this.sites[0].id
        }
      } catch (error) {
        console.error('Error fetching Webflow sites via action:', error)
        this.errorFetchingSites = `Failed to fetch sites: ${error?.response?.data?.message || error.message || 'Unknown error'}`
      } finally {
        this.isLoadingSites = false
      }
    },
    async triggerFetchCollections () {
      if (!this.isValidToFetchCollections) return

      this.isLoadingCollections = true
      this.collections = []
      this.selectedCollectionId = null
      this.errorFetchingCollections = null

      try {
        const fetchedCollections = await this.getWebflowCollections({
          contentplanId: this.contentplanId,
          siteId: this.selectedSiteId
        })
        this.collections = (fetchedCollections || []).map(collection => {
          const label = collection.displayName || collection.name || collection.singularName || 'Unnamed collection'
          return {
            ...collection,
            displayName: `${label} (${collection.id})`
          }
        })
        if (this.collections.length === 0) {
          this.errorFetchingCollections = 'No collections found for this site.'
        }
      } catch (error) {
        console.error('Error fetching Webflow collections via action:', error)
        this.errorFetchingCollections = `Failed to fetch collections: ${error?.response?.data?.message || error.message || 'Unknown error'}`
      } finally {
        this.isLoadingCollections = false
      }
    },
    async triggerFetchCollectionSchema () {
      if (!this.isValidToFetchSchema) return

      this.isLoadingSchema = true
      this.collectionSchemaFields = []
      this.selectedSchemaBody = null
      this.selectedSchemaSummary = null
      this.selectedSchemaImage = null
      this.errorFetchingSchema = null

      try {
        const fetchedFields = await this.getWebflowCollectionSchema({
          contentplanId: this.contentplanId,
          collectionId: this.selectedCollectionId
        })
        this.collectionSchemaFields = (fetchedFields || []).map(field => ({
          ...field,
          displayName: `${field.displayName || field.name} (${field.type})`
        }))
        if (this.collectionSchemaFields.length === 0) {
          this.errorFetchingSchema = 'No suitable (Rich Text, Plain Text, or Image) fields found in this collection.'
        }
      } catch (error) {
        console.error('Error fetching Webflow collection schema via action:', error)
        this.errorFetchingSchema = `Failed to fetch schema: ${error?.response?.data?.message || error.message || 'Unknown error'}`
      } finally {
        this.isLoadingSchema = false
      }
    },
    async saveIntegration () {
      if (!this.isValidToSave) return

      this.isLoading = true
      try {
        await this.postWebflowIntegration({
          contentplanId: this.contentplanId,
          siteId: this.selectedSiteId,
          collectionId: this.selectedCollectionId,
          wfSchemaBody: this.selectedSchemaBody,
          wfSchemaSummary: this.selectedSchemaSummary,
          wfSchemaImage: this.selectedSchemaImage,
          wfAutoPublish: this.wfAutoPublish,
          type: 'webflow'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Webflow integration saved!', type: 'success' })
        this.$emit('saved')
      } catch (error) {
        console.error('Error saving Webflow integration:', error)
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not save integration'}`, type: 'error' })
      } finally {
        this.isLoading = false
      }
    },
    async saveSettings () {
      if (!this.isAuthorized || this.isSavingSettings) return

      this.isSavingSettings = true
      try {
        await this.postWebflowIntegration({
          action: 'save-settings',
          contentplanId: this.contentplanId,
          wfAutoPublish: this.wfAutoPublish
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Webflow settings saved.', type: 'success' })
      } catch (error) {
        console.error('Error saving Webflow settings:', error)
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not save settings'}`, type: 'error' })
      } finally {
        this.isSavingSettings = false
      }
    },
    async handleDelete () {
      if (!this.isAuthorized || this.isDeleting) return
      if (!window.confirm('are you sure you want to remove integration')) return

      this.isDeleting = true
      try {
        await this.deleteIntegration({
          contentplanId: this.contentplanId,
          integrationType: 'webflow'
        })
        await this.getIntegrations({ contentplanId: this.contentplanId })
        this.setSnackbar({ open: true, text: 'Integration removed.', type: 'success' })
        if (this.workspaceId) {
          this.$router.push({
            name: 'ContentplanSettings',
            params: { contentplanId: this.contentplanId, workspaceId: this.workspaceId }
          })
        }
      } catch (error) {
        this.setSnackbar({ open: true, text: `Error: ${error?.response?.data?.message || error.message || 'Could not remove integration'}`, type: 'error' })
      } finally {
        this.isDeleting = false
      }
    },
    initializeFromIntegration (integration) {
      if (integration && integration.type === 'webflow') {
        this.sites = []
        this.collections = []
        this.collectionSchemaFields = []
        this.selectedSiteId = null
        this.selectedCollectionId = null
        this.selectedSchemaBody = null
        this.selectedSchemaSummary = null
        this.selectedSchemaImage = null
        this.sitesFetchSuccess = false

        this.selectedSiteId = integration.wf_site_id || this.initialSiteId || null
        this.selectedCollectionId = integration.wf_collection_id || null
        this.selectedSchemaBody = integration.wf_schema_body || null
        this.selectedSchemaSummary = integration.wf_schema_summary || null
        this.selectedSchemaImage = integration.wf_schema_image || null
        this.wfAutoPublish = !!integration.wf_auto_publish
      } else {
        this.sites = []
        this.collections = []
        this.collectionSchemaFields = []
        this.selectedSiteId = this.initialSiteId || null
        this.selectedCollectionId = null
        this.selectedSchemaBody = null
        this.selectedSchemaSummary = null
        this.selectedSchemaImage = null
        this.wfAutoPublish = false
        this.sitesFetchSuccess = false
        this.hasAutoFetchedSites = false
      }
    }
  },
  watch: {
    selectedSiteId (newValue) {
      this.collections = []
      this.collectionSchemaFields = []
      this.selectedCollectionId = null
      this.selectedSchemaBody = null
      this.selectedSchemaSummary = null
      this.selectedSchemaImage = null
      this.errorFetchingCollections = null
      this.errorFetchingSchema = null
      if (newValue && this.isAuthorized) {
        this.triggerFetchCollections()
      }
    },
    selectedCollectionId (newValue) {
      this.collectionSchemaFields = []
      this.selectedSchemaBody = null
      this.selectedSchemaSummary = null
      this.selectedSchemaImage = null
      this.errorFetchingSchema = null
      if (newValue && this.isAuthorized) {
        this.triggerFetchCollectionSchema()
      }
    },
    webflowIntegration: {
      immediate: true,
      handler (integration) {
        this.initializeFromIntegration(integration)
        if (integration?.type === 'webflow' && !this.hasAutoFetchedSites) {
          this.hasAutoFetchedSites = true
          this.triggerFetchSites()
        }
      }
    }
  },
  async mounted () {
    this.getIntegrations({ contentplanId: this.contentplanId })
  }
}
